import { useCallback, useEffect, useRef, useState } from "react";

/**
 * 动态计算滚动区域高度的Hook
 * 支持固定高度减去和动态元素高度减去
 */
export interface UseDynamicScrollHeightOptions {
  /** 固定减去的高度（像素） */
  fixedHeight?: number;
  /** 需要减去高度的元素ref */
  subtractElementRef?: React.RefObject<HTMLElement | null>;
  /** 额外的偏移量 */
  offset?: number;
  /** 最小高度限制 */
  minHeight?: number;
  /** 最大高度限制 */
  maxHeight?: number;
}

export interface UseDynamicScrollHeightReturn {
  /** 计算后的滚动区域高度 */
  scrollHeight: number;
  /** 用于绑定到滚动容器的ref */
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  /** 手动重新计算高度 */
  recalculate: () => void;
  /** 是否正在计算中 */
  isCalculating: boolean;
}

/**
 * 动态计算滚动区域高度的Hook
 *
 * @param options 配置选项
 * @returns 动态高度计算结果
 *
 * @example
 * ```tsx
 * const menuFooterRef = useRef<HTMLDivElement>(null);
 * const { scrollHeight, scrollContainerRef } = useDynamicScrollHeight({
 *   fixedHeight: 136,
 *   subtractElementRef: menuFooterRef,
 *   minHeight: 200,
 * });
 *
 * return (
 *   <div ref={scrollContainerRef} style={{ height: scrollHeight }}>
 *     // 滚动内容
 *   </div>
 * );
 * ```
 */
export const useDynamicScrollHeight = (
  options: UseDynamicScrollHeightOptions = {},
): UseDynamicScrollHeightReturn => {
  const {
    fixedHeight = 0,
    subtractElementRef,
    offset = 0,
    minHeight = 0,
    maxHeight = Infinity,
  } = options;

  const [scrollHeight, setScrollHeight] = useState<number>(0);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const rafIdRef = useRef<number | null>(null);

  /**
   * 计算动态高度
   * 使用 requestAnimationFrame 批处理布局读取，避免强制重排
   */
  const calculateHeight = useCallback(() => {
    // 取消之前的 RAF，避免重复计算
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
    }

    setIsCalculating(true);

    rafIdRef.current = requestAnimationFrame(() => {
      try {
        // 批量读取布局属性，避免强制同步重排
        const viewportHeight = window.innerHeight;
        let elementHeight = 0;

        // 一次性读取所有需要的布局属性
        if (subtractElementRef?.current) {
          elementHeight = subtractElementRef.current.offsetHeight;
        }

        // 计算固定减去的高度
        const totalSubtractHeight = fixedHeight + offset + elementHeight;

        // 计算最终高度
        let calculatedHeight = viewportHeight - totalSubtractHeight;

        // 应用最小和最大高度限制
        calculatedHeight = Math.max(calculatedHeight, minHeight);
        calculatedHeight = Math.min(calculatedHeight, maxHeight);

        setScrollHeight(calculatedHeight);
      } catch (error) {
        // 发生错误时使用默认高度
        setScrollHeight(
          Math.max(window.innerHeight - fixedHeight - offset, minHeight),
        );
      } finally {
        setIsCalculating(false);
        rafIdRef.current = null;
      }
    });
  }, [fixedHeight, subtractElementRef, offset, minHeight, maxHeight]);

  /**
   * 手动重新计算高度
   */
  const recalculate = useCallback(() => {
    calculateHeight();
  }, [calculateHeight]);

  // 初始化时计算高度
  useEffect(() => {
    calculateHeight();
  }, [calculateHeight]);

  // 监听窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      calculateHeight();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [calculateHeight]);

  // 监听需要减去的元素高度变化
  useEffect(() => {
    if (!subtractElementRef?.current) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      calculateHeight();
    });

    resizeObserver.observe(subtractElementRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [subtractElementRef, calculateHeight]);

  // 组件卸载时清理待处理的 RAF
  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, []);

  return {
    scrollHeight,
    scrollContainerRef,
    recalculate,
    isCalculating,
  };
};
